import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import MailIcon from "@mui/icons-material/Mail";
import Icons from "../../assets/icons";
import headerStyle from "./Header.module.css";
import { useNavigate } from "react-router-dom";
import { Collapse } from "@mui/material";
import callApi from "../../services/api/apiClient";
import { useSelector } from "react-redux";
import moment from "moment";
import { AccessTokenSlice } from "../../redux/reducers/reducer";
import { state } from "../../redux/util/getStore";
import { dispatchStore } from "../../lib/dispatchStore";
import ModalProvider from "../modal/ModalProvider";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function Header({ children }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedSubItem, setSelectedSubItem] = React.useState(null);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const [openSubMenu, setOpenSubMenu] = React.useState(null);
  const navigate = useNavigate();
  const userData = useSelector((state) => state?.user?.profile?.data);
  React.useEffect(() => {
    getUserInfo();
  }, []);

  const getUserInfo = () => {
    callApi("user/profile").loadingGroup("Header").executeDispatch();
  };

  const handleDrawerOpen = () => {
    setOpen(!open);
    // if (open === false) {
    //     setSubMenuOpen(false)
    // }
  };

  const handleSubMenuToggle = (menuTitle) => {
    setOpenSubMenu((prev) => (prev === menuTitle ? null : menuTitle));
  };

  const list = [
    { title: "Dashboard", icon: Icons.home, routeName: "/" },
    {
      title: "Report",
      icon: Icons.saleOrder,
      submenu: true,
      submenuItem: [
        { title: "Sale Order Report", route: "/sale_report" },
        { title: "Product and Category", route: "/product_report" },
        { title: "Sales Team", route: "/saleteam_report" },
      ],
    },
    {
      title: "Live Sale",
      icon: Icons.liveSale,
      submenu: true,
      submenuItem: [
        // { title: "Create Live Sale", route: "/create/livesale" },
        { title: "Live Sale Session List", route: "/livesale/sessionList" },
      ],
    },
    {
      title: "Customer List",
      icon: Icons.customer,
      routeName: "/customer_list",
    },
    {
      title: "Request Product",
      icon: Icons.requestProduct,
      routeName: "/product_list",
    },
    // {
    //   title: "Report Sale Order",
    //   icon: Icons.saleOrder,
    //   routeName: "/sale_order",
    // },
    {
      title: "Change Password",
      icon: Icons.changePassword,
      routeName: "/change_password",
    },
  ];

  const handleLogout = () => {
    const access_token = state("AccessToken");

    callApi("auth/logout")
      .withHeaders({
        "access-token": access_token,
      })
      .loadingGroup("logout")
      .execute()
      .then((res) => {
        if (res.status === "success") {
          dispatchStore(AccessTokenSlice.actions.setAccessToken(null));
          navigate("/login");
        }
      });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ bgcolor: "#ffffff" }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={
              {
                // marginRight: 5,
                // ...(open && { display: 'none' }),
              }
            }
          >
            <MenuIcon sx={{ color: "#164571" }} />
          </IconButton>
          <div className="ml-[15px]">
            <p className="text-lg font-interBold text-black-0">
              Welcome, {userData?.name}
            </p>
            <p className="text-sm font-interRegular text-gray-0">
              {moment().format("ddd, D MMM YYYY")}
            </p>
          </div>
          <div
            onClick={() => setOpenConfirmModal(true)}
            className="ml-auto inline-flex"
          >
            <LogoutIcon sx={{ color: "#164571" }} />
          </div>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open} classes={headerStyle.myDrawer}>
        <DrawerHeader sx={{ bgcolor: "#164571", py: "8px" }}>
          <div className="inline-flex justify-center items-center">
            <img
              src={Icons.marigoldCircle}
              alt="marigoldCircle"
              className={`float-left cursor-pointer h-[64px]`}
            />
            <img
              src={Icons.marigoldText}
              alt="marigoldText"
              className={`w-[142px] h-[60px] origin-left`}
            />
          </div>
        </DrawerHeader>
        <List
          sx={{
            width: "100%",
            height: "100%",
            maxWidth: 400,
            bgcolor: "#164571",
          }}
        >
          {list.map((item, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{ display: "block", bgcolor: "#164571" }}
            >
              <ListItemButton
                onClick={() => {
                  setSelectedItem(item.title);
                  setSelectedSubItem(null);
                  if (item?.submenu) {
                    handleSubMenuToggle(item.title); // Toggle the submenu
                  } else {
                    navigate(item.routeName);
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? "8px" : "auto",
                    justifyContent: "center",
                  }}
                >
                  <img src={item.icon} alt="" className="w-[20px] h-[20px]" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontFamily:
                          selectedItem === item.title
                            ? "InterSemiBold"
                            : "InterRegular",
                        color: "#ffffff",
                      }}
                    >
                      {item.title}
                    </Typography>
                  }
                  sx={{ opacity: open ? 1 : 0 }}
                />
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                    opacity: open ? 1 : 0,
                  }}
                >
                  <img
                    src={Icons.rightArrow}
                    alt="rightArrow"
                    className={`w-[24px] h-[24px] ${
                      item?.submenu && openSubMenu === item.title && open
                        ? "rotate-90"
                        : ""
                    }`}
                  />
                </ListItemIcon>
              </ListItemButton>
              {item?.submenu && open && openSubMenu === item.title && (
                <Collapse
                  in={openSubMenu === item.title}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div">
                    {item?.submenuItem?.map((subItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        disablePadding
                        sx={{ display: "block" }}
                      >
                        <ListItemButton
                          sx={{ pl: 6 }}
                          onClick={() => {
                            setSelectedSubItem(subItem.title);
                            if (selectedItem !== "Live Sale") {
                              setSelectedItem(null);
                            }
                            navigate(subItem.route);
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#ffffff",
                                  fontFamily:
                                    selectedSubItem === subItem?.title
                                      ? "InterSemiBold"
                                      : "InterRegular",
                                }}
                              >
                                {subItem.title}
                              </Typography>
                            }
                          />
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={Icons.rightArrow}
                              alt="rightArrow"
                              className={`w-[14px] h-[14px]`}
                            />
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1 }}>
        <DrawerHeader />
        {children}
      </Box>
      <ModalProvider
        isOpen={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title={"Logout Confirmation"}
        cancelBtn={() => setOpenConfirmModal(false)}
        sendBtn={handleLogout}
        description={"Are you sure you want to logout?"}
      />
    </Box>
  );
}
